import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  addRefundJSON,
  addRefundSource,
  addReturnedProducts,
  getRefundJSON,
  getRefundSource,
} from "features/returnedProducts/returnedProductSlice";
import { getSelectedOrder } from "features/orders/orderSlice";
import { verifyBankDetailsApi } from "utils/api";
import { toast } from "react-toastify";
import {
  StyledPaymentDiv,
  StyledPaymentMode,
} from "components/v2/ReturnCart/styled";
import CheckmarkControl from "components/v2/Common/CheckmarkControl";
// import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ReactComponent as UPIIcon } from "assets/upi.svg";
// import { ReactComponent as BankIcon } from "assets/bank.svg";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
// import { ReactComponent as CouponIcon } from "assets/coupon.svg";
// import { ReactComponent as WalletIcon } from "assets/wallet.svg";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import InfoIcon from "@mui/icons-material/Info";
import { getStoreCreditSource } from "utils/refundConfig";
import {
  getReasonFirstRefundAmount,
  isReasonFirstQuoteMethodAvailable,
  itemAllowsRefundMethod,
  reasonFirstMethodIsAllowed,
} from "utils/reasonFirstRefund";
import { V2PaymentDetailsBoneyardLoader } from "components/v2/Loaders/BoneyardLoaders";
import { normalizePhoneNumber } from "utils/contactValidation";
import { getApiErrorMessage } from "utils/apiError";
import { mergeReturnCartContext } from "utils/returnCartSession";
import {
  IykaRefundBreakdown,
  isIykaPolicyQuote,
} from "components/common/IykaRefundBreakdown";

const BANK_VERIFICATION_FALLBACK = "Something is wrong. Please try again.";
const BANK_ACCOUNT_PATTERN = /^\d{9,18}$/;
const ACCOUNT_HOLDER_NAME_PATTERN = /^[A-Za-z ]+$/;
const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const UPI_PATTERN = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;
const mergeReasonFirstTransferDetails = (current = {}, update = {}) => {
  const next = { ...current };
  delete next.refund_method;
  return { ...next, ...update };
};

export const PriceBox = ({
  refundSource,
  accHolderName,
  accountNumber,
  confirmAccNumber,
  ifsc,
  upiValue,
  paymentDetails,
  calculatingLoader,
  returnedProducts,
  userPhoneNumber,
  onReasonFirstRefundMethodChange,
}) => {
  const dispatch = useDispatch();
  const product_applicable_refund_modes =
    returnedProducts &&
    returnedProducts.length > 0 &&
    returnedProducts
      .filter((a) => a.return_type === "refund")
      .map((a) => a.applicable_refund_modes)
      .flat();

  // Selectors
  const rmsConfig = useSelector(getRmsConfig);
  const selectedOrder = useSelector(getSelectedOrder);
  const verificationPhone = normalizePhoneNumber(
    userPhoneNumber === undefined
      ? selectedOrder?.user_phone_number
      : userPhoneNumber,
  );
  const merchantConfig = useSelector(getMerchantConfig);
  const refundDisplay =
    Number(rmsConfig.merchant_id ?? merchantConfig.merchant_id) === 2164
      ? rmsConfig.refund_display
      : null;
  const getRefundMethodDisplay = (sourceValue) => {
    const mode = {
      source_refund: "refund_to_source",
      payment_link: "bank_transfer",
      shopify_store_credit: "store_credit",
      credits_yard: "store_credit",
      easyrewardz: "store_credit",
    }[sourceValue] || sourceValue;
    return refundDisplay?.methods?.[mode];
  };

  // States
  const [submittingDetails, setSubmitttingDetails] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const hasUserSelectedRefundModeRef = useRef(false);
  const hasSyncedValidInlineBankRef = useRef(false);
  const hasSyncedValidInlineUpiRef = useRef(false);
  const [inlineBankDetails, setInlineBankDetails] = useState({
    accountHolderName: accHolderName || "",
    accountNumber: accountNumber || "",
    confirmAccountNumber: confirmAccNumber || "",
    ifsc: ifsc || "",
  });
  const [inlineUpiValue, setInlineUpiValue] = useState(upiValue || "");
  const [inlineBankTouched, setInlineBankTouched] = useState({});
  const [inlineUpiTouched, setInlineUpiTouched] = useState(false);
  const [inlineEditingMethod, setInlineEditingMethod] = useState(null);
  const [refundModesExpanded, setRefundModesExpanded] = useState(!refundSource);
  const [savedInlineRefundDetails, setSavedInlineRefundDetails] = useState({
    bank_transfer:
      accHolderName && accountNumber && confirmAccNumber && ifsc
        ? {
            accountHolderName: accHolderName,
            accountNumber,
            confirmAccountNumber: confirmAccNumber,
            ifsc,
            verified: !!rmsConfig.bank_details_verification_enabled,
          }
        : null,
    upi: upiValue
      ? {
          upi: upiValue,
          verified: !!rmsConfig.bank_details_verification_enabled,
        }
      : null,
  });

  useEffect(() => {
    if (accHolderName && accountNumber && confirmAccNumber && ifsc) {
      setSavedInlineRefundDetails((currentDetails) => ({
        ...currentDetails,
        bank_transfer: {
          accountHolderName: accHolderName,
          accountNumber,
          confirmAccountNumber: confirmAccNumber,
          ifsc,
          verified: !!rmsConfig.bank_details_verification_enabled,
        },
      }));
      setInlineBankDetails({
        accountHolderName: accHolderName,
        accountNumber,
        confirmAccountNumber: confirmAccNumber,
        ifsc,
      });
    }

    if (upiValue) {
      setSavedInlineRefundDetails((currentDetails) => ({
        ...currentDetails,
        upi: {
          upi: upiValue,
          verified: !!rmsConfig.bank_details_verification_enabled,
        },
      }));
      setInlineUpiValue(upiValue);
    }
  }, [
    accHolderName,
    accountNumber,
    confirmAccNumber,
    ifsc,
    rmsConfig.bank_details_verification_enabled,
    upiValue,
  ]);
  const selectedRefundSource = useSelector(getRefundSource);
  const currentRefundJSON = useSelector(getRefundJSON);
  const orderApplicableRefundModes = Array.isArray(
    selectedOrder?.applicable_refund_modes,
  )
    ? selectedOrder.applicable_refund_modes.filter(Boolean)
    : selectedOrder?.applicable_refund_modes
      ? [selectedOrder.applicable_refund_modes]
      : [];
  const refundModeRestrictedProducts =
    returnedProducts?.filter((item) => item.return_type === "refund") || [];
  const reasonFirstRefundProducts = refundModeRestrictedProducts.filter(
    (item) => item.reason_mode === "reason_first",
  );
  const legacyRefundProducts = refundModeRestrictedProducts.filter(
    (item) => item.reason_mode !== "reason_first",
  );
  const hasLegacyRefundProducts = legacyRefundProducts.length > 0;
  const refundBucket = selectedOrder?.is_cod || (
    rmsConfig.partially_paid_order_as_cod &&
    selectedOrder?.financial_status === "partially_paid"
  )
    ? "cod"
    : selectedOrder?.financial_status === "partially_paid"
      ? "partial"
      : "prepaid";
  const reasonAllowsRefundMethod = (method, item = null) => {
    if (!item && reasonFirstRefundProducts.length > 0) return true;
    const items = item ? [item] : reasonFirstRefundProducts;
    return items.every((item) =>
      reasonFirstMethodIsAllowed(item, refundBucket, method),
    );
  };
  const productApplicableRefundModes = Array.isArray(
    product_applicable_refund_modes,
  )
    ? product_applicable_refund_modes.filter(Boolean)
    : [];
  const hasOrderRefundModeRestrictions =
    Array.isArray(selectedOrder?.applicable_refund_modes) &&
    selectedOrder.applicable_refund_modes.filter(Boolean).length > 0;
  const hasProductRefundModeRestrictions = refundModeRestrictedProducts.some(
    (item) =>
      Array.isArray(item?.applicable_refund_modes) &&
      item.applicable_refund_modes.filter(Boolean).length > 0,
  );
  const includesAnyRefundMode = (availableModes, aliases) =>
    aliases.some((alias) => availableModes.includes(alias));
  const orderAllowsRefundMode = (...aliases) =>
    !hasOrderRefundModeRestrictions ||
    includesAnyRefundMode(orderApplicableRefundModes, aliases);
  const productAllowsRefundMode = (...aliases) =>
    reasonFirstRefundProducts.length > 0 ||
    !hasProductRefundModeRestrictions ||
    includesAnyRefundMode(productApplicableRefundModes, aliases);
  const allowsSourceRefundMode =
    orderAllowsRefundMode("refund_to_source", "source_refund") &&
    productAllowsRefundMode("refund_to_source", "source_refund");
  const allowsGiftCardRefundMode =
    orderAllowsRefundMode("gift_card") && productAllowsRefundMode("gift_card");
  const allowsCouponRefundMode =
    orderAllowsRefundMode("coupon_code") &&
    productAllowsRefundMode("coupon_code");
  const allowsStoreCreditRefundMode =
    orderAllowsRefundMode("store_credit") &&
    productAllowsRefundMode("store_credit");
  const allowsShopifyStoreCreditRefundMode =
    orderAllowsRefundMode("shopify_store_credit") &&
    productAllowsRefundMode("shopify_store_credit");
  const resolvedStoreCreditSource = getStoreCreditSource(rmsConfig);
  const bankTransferMode = rmsConfig?.refund_config?.modes?.bank_transfer;
  const upiMode = rmsConfig?.refund_config?.modes?.upi;
  const bankTransferAvailable = rmsConfig?.refund_config?.modes
    ? bankTransferMode?.enabled !== false &&
      bankTransferMode?.available !== false
    : selectedOrder.is_cod
      ? !!rmsConfig.collect_bank_details_for_cod_orders
      : !!rmsConfig.customer_bank_enabled;
  const upiAvailable = rmsConfig?.refund_config?.modes
    ? upiMode?.enabled !== false && upiMode?.available !== false
    : selectedOrder.is_cod
      ? !!rmsConfig.collect_upi_details_for_cod_orders
      : !!rmsConfig.customer_upi_enabled;
  const allowsBankOrUpiRefundMode =
    (hasProductRefundModeRestrictions ||
      !hasOrderRefundModeRestrictions ||
      orderAllowsRefundMode("bank_transfer", "payment_link")) &&
    productAllowsRefundMode("bank_transfer", "payment_link");

  const shouldPopulateTransferDetails =
    hasProductRefundModeRestrictions ||
    !hasOrderRefundModeRestrictions ||
    (orderApplicableRefundModes.includes("payment_link") &&
      (!hasProductRefundModeRestrictions ||
        productApplicableRefundModes.includes("payment_link")));
  const shouldCollectRefundDetailsInline = useCallback(
    (source) => source === "bank_transfer" || source === "upi",
    [],
  );
  const saveLegacyRefundJSON = (source, details) => {
    const transferDetails = details || {};
    const nextRefundJSON = reasonFirstRefundProducts.length > 0 && hasLegacyRefundProducts
      ? { ...currentRefundJSON, ...transferDetails }
      : { ...transferDetails, refund_method: source };
    if (reasonFirstRefundProducts.length > 0 && hasLegacyRefundProducts) {
      delete nextRefundJSON.refund_method;
    }
    dispatch(addRefundJSON(nextRefundJSON));
    mergeReturnCartContext({ refundSource: source, refundJSON: nextRefundJSON });
    return nextRefundJSON;
  };
  const getInlineBankErrors = useCallback((details = inlineBankDetails) => {
    const nextErrors = {};

    if (!details.accountHolderName.trim()) {
      nextErrors.accountHolderName = "Account holder name is required";
    } else if (details.accountHolderName.trim().length < 2) {
      nextErrors.accountHolderName = "Name length must be more than 2";
    } else if (!ACCOUNT_HOLDER_NAME_PATTERN.test(details.accountHolderName.trim())) {
      nextErrors.accountHolderName = "Remove special characters";
    }

    if (!BANK_ACCOUNT_PATTERN.test(details.accountNumber)) {
      nextErrors.accountNumber = "Account number must be 9-18 digits";
    }

    if (!BANK_ACCOUNT_PATTERN.test(details.confirmAccountNumber)) {
      nextErrors.confirmAccountNumber = "Account number must be 9-18 digits";
    } else if (details.confirmAccountNumber !== details.accountNumber) {
      nextErrors.confirmAccountNumber = "Account numbers do not match";
    }

    if (!IFSC_PATTERN.test(details.ifsc.toUpperCase())) {
      nextErrors.ifsc = "Enter a valid IFSC code";
    }

    return nextErrors;
  }, [inlineBankDetails]);
  const getInlineUpiError = useCallback(
    (value = inlineUpiValue) => {
      if (!value.trim()) {
        return "UPI ID is required";
      }

      if (/@paytm$/i.test(value)) {
        return "Paytm UPI IDs are not supported";
      }

      if (!UPI_PATTERN.test(value)) {
        return "Invalid UPI ID";
      }

      return "";
    },
    [inlineUpiValue],
  );
  const inlineBankErrors = getInlineBankErrors(inlineBankDetails);
  const getVisibleInlineBankError = (fieldName) =>
    inlineBankTouched[fieldName] ? inlineBankErrors[fieldName] || "" : "";
  const visibleInlineUpiError = inlineUpiTouched
    ? getInlineUpiError(inlineUpiValue)
    : "";
  const markInlineBankFieldTouched = (fieldName) => {
    setInlineBankTouched((currentTouched) => ({
      ...currentTouched,
      [fieldName]: true,
    }));
  };
  const syncInlineBankDetails = (details) => {
    const normalizedDetails = {
      ...details,
      ifsc: details.ifsc.toUpperCase(),
    };
    setInlineBankDetails(normalizedDetails);
  };
  const syncInlineUpiDetails = (value) => {
    setInlineUpiValue(value);
  };
  const getInlineSubmitLabel = () => {
    if (submittingDetails) {
      return rmsConfig.bank_details_verification_enabled
        ? "Verifying..."
        : "Saving...";
    }

    return rmsConfig.bank_details_verification_enabled
      ? "Verify & save"
      : "Save details";
  };
  const getInlineSavedLabel = (method) =>
    savedInlineRefundDetails[method]?.verified
      ? "Details verified and saved"
      : "Details added";
  const handleSaveInlineBankDetails = async (event) => {
    event.stopPropagation();
    setInlineBankTouched({
      accountHolderName: true,
      accountNumber: true,
      confirmAccountNumber: true,
      ifsc: true,
    });
    const nextErrors = getInlineBankErrors(inlineBankDetails);
    if (Object.keys(nextErrors).length > 0 || submittingDetails) {
      return;
    }

    setSubmitttingDetails(true);
    setVerificationError("");
    const normalizedDetails = {
      ...inlineBankDetails,
      accountHolderName: inlineBankDetails.accountHolderName.trim(),
      ifsc: inlineBankDetails.ifsc.toUpperCase(),
    };

    try {
      if (rmsConfig.bank_details_verification_enabled) {
        await verifyBankDetailsApi({
          ifsc: normalizedDetails.ifsc,
          account_number: Number(normalizedDetails.accountNumber),
          name: normalizedDetails.accountHolderName,
          phone: verificationPhone ? Number(verificationPhone) : null,
          vpa: null,
        });
      }

      setSavedInlineRefundDetails((currentDetails) => ({
        ...currentDetails,
        bank_transfer: {
          ...normalizedDetails,
          verified: !!rmsConfig.bank_details_verification_enabled,
        },
      }));
      setInlineEditingMethod(null);
      setRefundModesExpanded(false);
      hasSyncedValidInlineBankRef.current = true;
      const nextRefundJSON = isReasonFirstRefundFlow
        ? {
            ...mergeReasonFirstTransferDetails(currentRefundJSON, {
            bank_details: {
              bank_account_number: normalizedDetails.accountNumber,
              confirm_bank_account_number: normalizedDetails.confirmAccountNumber,
              bank_account_holder_name: normalizedDetails.accountHolderName,
              bank_ifsc_code: normalizedDetails.ifsc,
            },
            }),
          }
        : {
        bank_details: {
          bank_account_number: normalizedDetails.accountNumber,
          confirm_bank_account_number:
            normalizedDetails.confirmAccountNumber,
          bank_account_holder_name: normalizedDetails.accountHolderName,
          bank_ifsc_code: normalizedDetails.ifsc,
        },
        refund_method: "bank_transfer",
      };
      dispatch(addRefundJSON(nextRefundJSON));
      mergeReturnCartContext(isReasonFirstRefundFlow
        ? { refundJSON: nextRefundJSON }
        : { refundJSON: nextRefundJSON, refundSource: "bank_transfer" });
      toast.success(
        rmsConfig.bank_details_verification_enabled
          ? "Details have been verified and saved"
          : "Details have been saved",
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, BANK_VERIFICATION_FALLBACK);
      setVerificationError(errorMessage);
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setSubmitttingDetails(false);
    }
  };
  const handleSaveInlineUpiDetails = async (event) => {
    event.stopPropagation();
    setInlineUpiTouched(true);
    const nextError = getInlineUpiError(inlineUpiValue);
    if (nextError || submittingDetails) {
      return;
    }

    setSubmitttingDetails(true);
    setVerificationError("");

    try {
      if (rmsConfig.bank_details_verification_enabled) {
        await verifyBankDetailsApi({
          ifsc: null,
          account_number: null,
          name: null,
          phone: verificationPhone ? Number(verificationPhone) : null,
          vpa: inlineUpiValue,
        });
      }

      setSavedInlineRefundDetails((currentDetails) => ({
        ...currentDetails,
        upi: {
          upi: inlineUpiValue,
          verified: !!rmsConfig.bank_details_verification_enabled,
        },
      }));
      setInlineEditingMethod(null);
      setRefundModesExpanded(false);
      hasSyncedValidInlineUpiRef.current = true;
      const nextRefundJSON = isReasonFirstRefundFlow
        ? {
            ...mergeReasonFirstTransferDetails(currentRefundJSON, {
            upi: { upi_code: inlineUpiValue },
            }),
          }
        : {
        upi: {
          upi_code: inlineUpiValue,
        },
        refund_method: "upi",
      };
      dispatch(addRefundJSON(nextRefundJSON));
      mergeReturnCartContext(isReasonFirstRefundFlow
        ? { refundJSON: nextRefundJSON }
        : { refundJSON: nextRefundJSON, refundSource: "upi" });
      toast.success(
        rmsConfig.bank_details_verification_enabled
          ? "Details have been verified and saved"
          : "Details have been saved",
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, BANK_VERIFICATION_FALLBACK);
      setVerificationError(errorMessage);
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setSubmitttingDetails(false);
    }
  };

  function updateRefundSource(source, shouldHandleTransferDetails = false) {
    hasUserSelectedRefundModeRef.current = true;
    hasSyncedValidInlineBankRef.current = false;
    hasSyncedValidInlineUpiRef.current = false;
    const shouldKeepExpandedForDetails =
      shouldHandleTransferDetails &&
      shouldCollectRefundDetailsInline(source) &&
      !savedInlineRefundDetails[source];
    setRefundModesExpanded(shouldKeepExpandedForDetails);
    if (source === "bank_transfer") {
      setInlineBankTouched({});
    }
    if (source === "upi") {
      setInlineUpiTouched(false);
    }
    dispatch(addRefundSource(source));
    if (reasonFirstRefundProducts.length > 0 && hasLegacyRefundProducts) {
      const nextRefundJSON = { ...currentRefundJSON };
      delete nextRefundJSON.refund_method;
      dispatch(addRefundJSON(nextRefundJSON));
      mergeReturnCartContext({ refundJSON: nextRefundJSON });
    }

    if (
      !shouldHandleTransferDetails ||
      (source !== "bank_transfer" && source !== "upi")
    ) {
      return;
    }

    if (
      !rmsConfig.use_direct_transfer_for_refund &&
      shouldPopulateTransferDetails
    ) {
      if (source === "upi") {
        saveLegacyRefundJSON(source, { upi: { upi_code: upiValue } });
      } else {
        saveLegacyRefundJSON(source, { bank_details: {
          bank_account_number: accountNumber,
          confirm_bank_account_number: confirmAccNumber,
          bank_account_holder_name: accHolderName,
          bank_ifsc_code: ifsc,
        } });
      }
    }

    if (shouldCollectRefundDetailsInline(source)) {
      if (savedInlineRefundDetails[source]) {
        if (source === "upi") {
          saveLegacyRefundJSON(source, { upi: { upi_code: savedInlineRefundDetails.upi.upi } });
        } else {
          saveLegacyRefundJSON(source, { bank_details: {
            bank_account_number: savedInlineRefundDetails.bank_transfer.accountNumber,
            confirm_bank_account_number: savedInlineRefundDetails.bank_transfer.confirmAccountNumber,
            bank_account_holder_name: savedInlineRefundDetails.bank_transfer.accountHolderName,
            bank_ifsc_code: savedInlineRefundDetails.bank_transfer.ifsc,
          } });
        }
        setInlineEditingMethod(null);
        return;
      }

      if (reasonFirstRefundProducts.length > 0 && hasLegacyRefundProducts) {
        const nextRefundJSON = { ...currentRefundJSON };
        delete nextRefundJSON[source === "upi" ? "upi" : "bank_details"];
        dispatch(addRefundJSON(nextRefundJSON));
        mergeReturnCartContext({ refundJSON: nextRefundJSON });
      } else {
        dispatch(addRefundJSON({}));
      }
      setInlineEditingMethod(source);
      return;
    }

  }

  const BonusChargesSection = paymentDetails?.applicable_offer_amount > 0 && (
    <p className="refund-mode-offer-label">
      + ₹ {paymentDetails?.applicable_offer_amount}{" "}
    </p>
  );

  // Utility function to get charges map
  const getSourceChargesMap = (all_mode_charges) => {
    if (!all_mode_charges || Object.keys(all_mode_charges).length === 0) {
      return {};
    }

    if (all_mode_charges.all_modes) {
      return {
        bank_transfer: all_mode_charges.all_modes || 0,
        upi: all_mode_charges.all_modes || 0,
        refund_to_source: all_mode_charges.all_modes || 0,
        coupon: all_mode_charges.all_modes || 0,
        gift_card: all_mode_charges.all_modes || 0,
        store_credit: all_mode_charges.all_modes || 0,
        shopify_store_credit: all_mode_charges.all_modes || 0,
      };
    }

    return {
      bank_transfer: all_mode_charges.bank_transfer || 0,
      upi: all_mode_charges.bank_transfer || 0,
      refund_to_source: all_mode_charges.source_refund || 0,
      coupon: all_mode_charges.coupon || 0,
      gift_card: all_mode_charges.gift_card || 0,
      store_credit: all_mode_charges.store_credit || 0,
      shopify_store_credit: all_mode_charges.shopify_store_credit || 0,
    };
  };

  // ChargesSection component
  const ChargesSection = ({ source }) => {
    const { all_mode_charges } = paymentDetails || {};
    const sourceMap = getSourceChargesMap(all_mode_charges);

    if (sourceMap[source] > 0) {
      return <p className="handling-charges">- ₹ {sourceMap[source]}</p>;
    }

    return null; // Explicitly return null when there's no content
  };

  // getCharges function
  const getCharges = (source, paymentDetails) => {
    const { all_mode_charges } = paymentDetails || {};
    const sourceMap = getSourceChargesMap(all_mode_charges);
    return sourceMap[source] || 0;
  };

  const renderIcon = (sourceValue) => {
    const icons = {
      gift_card: <CardGiftcardIcon style={{ fontSize: "20px" }} />,
      upi: <UPIIcon style={{ fontSize: "14px" }} />,
      bank_transfer: <AccountBalanceIcon style={{ fontSize: "18px" }} />,
      refund_to_source: <AccountBalanceIcon style={{ fontSize: "14px" }} />,
      coupon: <ConfirmationNumberIcon style={{ fontSize: "18px" }} />,
      default: <AccountBalanceWalletIcon style={{ fontSize: "18px" }} />,
    };
    return icons[sourceValue] || icons.default;
  };

  const getRefundModeTitle = (sourceValue, sourceLabel) => {
    if (sourceValue === "refund_to_source") {
      return "Original Payment Method";
    }
    if (sourceValue === "bank_transfer") {
      return "Bank Transfer";
    }
    if (sourceValue === "upi") {
      return "UPI";
    }
    if (
      sourceValue === "store_credit" ||
      sourceValue === "shopify_store_credit"
    ) {
      return sourceLabel || "Store Credit";
    }
    return sourceLabel;
  };

  const getRefundModeDescription = (sourceValue) => {
    const description = getRefundMethodDisplay(sourceValue)?.description;
    if (description) return description;
    if (sourceValue === "refund_to_source") {
      return "Refund to your original payment method in 3-5 days.";
    }
    if (sourceValue === "bank_transfer") {
      return "Refund directly to your bank account.";
    }
    if (sourceValue === "upi") {
      return "Receive the refund in your preferred UPI account.";
    }
    if (
      sourceValue === "store_credit" ||
      sourceValue === "credits_yard" ||
      sourceValue === "shopify_store_credit"
    ) {
      return "Added to your wallet immediately.";
    }
    if (sourceValue === "gift_card") {
      return "Receive a gift card you can use on your next purchase.";
    }
    if (sourceValue === "coupon") {
      return "Get a refund coupon for a future order.";
    }
    return "Select this method to receive your refund.";
  };

  const visibleRefundModeOptions = useMemo(() => {
    const options = [];

    if (bankTransferAvailable && allowsBankOrUpiRefundMode && reasonAllowsRefundMethod("bank_transfer")) {
      options.push({
        label: "Bank Account",
        value: "bank_transfer",
      });
    }
    if (upiAvailable && allowsBankOrUpiRefundMode && reasonAllowsRefundMethod("upi")) {
      options.push({
        label: "UPI",
        value: "upi",
      });
    }
    if (rmsConfig.coupon_refund_enabled && allowsCouponRefundMode && reasonAllowsRefundMethod("coupon")) {
      options.push({
        label: rmsConfig.coupon_method_name,
        value: "coupon",
      });
    }
    if (rmsConfig.refund_to_source && allowsSourceRefundMode && reasonAllowsRefundMethod("refund_to_source")) {
      options.push({
        label: "Source Account",
        value: "refund_to_source",
      });
    }
    if (
      rmsConfig.store_credit &&
      resolvedStoreCreditSource &&
      allowsStoreCreditRefundMode &&
      reasonAllowsRefundMethod("store_credit")
    ) {
      options.push({
        label: rmsConfig.store_credit_method_name,
        value: resolvedStoreCreditSource,
      });
    }
    if (rmsConfig.shopify_store_credit && allowsShopifyStoreCreditRefundMode && reasonAllowsRefundMethod("shopify_store_credit")) {
      options.push({
        label: "Shopify Store Credits",
        value: "shopify_store_credit",
      });
    }
    if (rmsConfig.gift_card && allowsGiftCardRefundMode && reasonAllowsRefundMethod("gift_card")) {
      options.push({
        label: rmsConfig.gift_card_method_name,
        value: "gift_card",
      });
    }

    if (options.length === 0 && allowsBankOrUpiRefundMode && reasonAllowsRefundMethod("bank_transfer")) {
      options.push({
        label: "Bank Account",
        value: "bank_transfer",
      });
    }

    return options;
  }, [
    allowsBankOrUpiRefundMode,
    allowsCouponRefundMode,
    allowsGiftCardRefundMode,
    allowsShopifyStoreCreditRefundMode,
    allowsSourceRefundMode,
    allowsStoreCreditRefundMode,
    reasonFirstRefundProducts,
    refundBucket,
    bankTransferAvailable,
    rmsConfig.coupon_refund_enabled,
    rmsConfig.gift_card,
    rmsConfig.gift_card_method_name,
    rmsConfig.refund_to_source,
    rmsConfig.shopify_store_credit,
    rmsConfig.store_credit,
    rmsConfig.store_credit_method_name,
    rmsConfig.coupon_method_name,
    resolvedStoreCreditSource,
    upiAvailable,
  ]);

  const getReasonFirstQuoteItem = (item, itemIndex) => {
    const quoteItems = paymentDetails?.line_items || [];
    const indexedItem = quoteItems[itemIndex];
    if (String(indexedItem?.line_item_id) === String(item?.line_item_id)) return indexedItem;
    return quoteItems.find((lineItem) =>
      String(lineItem?.line_item_id) === String(item?.line_item_id),
    );
  };

  const getItemRefundModeOptions = (item, itemIndex, includeQuoteMethods = true) =>
    visibleRefundModeOptions.filter((option) => {
      const aliases = option.value === "coupon"
        ? ["coupon", "coupon_code"]
        : option.value === "refund_to_source"
          ? ["refund_to_source", "source_refund"]
          : option.value === "bank_transfer"
            ? ["bank_transfer", "payment_link"]
            : option.value === "credits_yard" || option.value === "easyrewardz"
              ? [option.value, "store_credit"]
              : option.value === "shopify_store_credit"
                ? [option.value, "store_credit"]
                : [option.value];
      const quoteAllowsMethod = !includeQuoteMethods || isReasonFirstQuoteMethodAvailable(
        getReasonFirstQuoteItem(item, itemIndex),
        option.value,
      );
      return (
        quoteAllowsMethod &&
        reasonFirstMethodIsAllowed(item, refundBucket, option.value) &&
        reasonAllowsRefundMethod(option.value, item) &&
        itemAllowsRefundMethod(item, ...aliases)
      );
    });

  const attemptedMethodNormalizationRef = useRef(new Set());
  useEffect(() => {
    if (reasonFirstRefundProducts.length === 0) return;
    let didChange = false;
    let hasNewNormalization = false;
    const nextProducts = returnedProducts.map((item) => ({ ...item }));
    const normalizationKeys = [];
    returnedProducts.forEach((item, itemIndex) => {
      if (item.return_type !== "refund" || item.reason_mode !== "reason_first") return;
      const options = getItemRefundModeOptions(item, itemIndex);
      const policyOptions = getItemRefundModeOptions(item, itemIndex, false);
      const selected = item.customer_preferred_refund_method;
      const quoteItem = getReasonFirstQuoteItem(item, itemIndex);
      if (
        selected &&
        policyOptions.some((option) => option.value === selected) &&
        !isReasonFirstQuoteMethodAvailable(quoteItem, selected)
      ) return;
      if (selected && options.some((option) => option.value === selected)) return;
      const quoteSelectedMethod = getReasonFirstQuoteItem(item, itemIndex)?.customer_preferred_refund_method;
      const nextMethod = quoteSelectedMethod && options.some((option) => option.value === quoteSelectedMethod)
        ? quoteSelectedMethod
        : options.length === 1 ? options[0].value : null;
      if (selected !== nextMethod) {
        nextProducts[itemIndex].customer_preferred_refund_method = nextMethod;
        didChange = true;
        const itemKey = item.line_item_id || itemIndex;
        const normalizationKey = `${itemKey}:${selected ?? "<unset>"}->${nextMethod ?? "<none>"}`;
        normalizationKeys.push(normalizationKey);
        if (!attemptedMethodNormalizationRef.current.has(normalizationKey)) {
          hasNewNormalization = true;
        }
      }
    });
    if (!didChange) {
      attemptedMethodNormalizationRef.current.clear();
    } else if (hasNewNormalization) {
      normalizationKeys.forEach((key) => attemptedMethodNormalizationRef.current.add(key));
      if (onReasonFirstRefundMethodChange) {
        onReasonFirstRefundMethodChange(nextProducts);
      } else {
        dispatch(addReturnedProducts(nextProducts));
      }
    }
    // Only re-evaluate when the policy/selection inputs change. The ref also
    // makes the callback idempotent if a parent rerenders without persisting
    // the normalized selection immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonFirstRefundProducts, returnedProducts, visibleRefundModeOptions, paymentDetails]);

  const isReasonFirstRefundFlow = reasonFirstRefundProducts.length > 0;
  const isReasonFirstOnlyRefundFlow = isReasonFirstRefundFlow && !hasLegacyRefundProducts;
  const editReasonFirstTransferDetails = (method) => {
    const nextRefundJSON = { ...currentRefundJSON };
    delete nextRefundJSON[method === "bank_transfer" ? "bank_details" : "upi"];
    delete nextRefundJSON.refund_method;
    dispatch(addRefundJSON(nextRefundJSON));
    mergeReturnCartContext({ refundJSON: nextRefundJSON });
    setInlineEditingMethod(method);
  };
  const refundModeName = (method) => {
    const configuredLabels = {
      bank_transfer: "Bank Account",
      upi: "UPI",
      coupon: rmsConfig.coupon_method_name || "Coupon",
      coupon_code: rmsConfig.coupon_method_name || "Coupon",
      gift_card: rmsConfig.gift_card_method_name || "Gift Card",
      store_credit: rmsConfig.store_credit_method_name || "Store Credit",
      credits_yard: rmsConfig.store_credit_method_name || "Store Credit",
      easyrewardz: rmsConfig.store_credit_method_name || "Store Credit",
      shopify_store_credit: "Shopify Store Credits",
      refund_to_source: "Source Account",
      source_refund: "Source Account",
    };
    return configuredLabels[method] || method;
  };
  const reasonFirstRefundBreakdown = returnedProducts
    .map((item, itemIndex) => {
      if (item.return_type !== "refund" || item.reason_mode !== "reason_first") return null;
      const quoteItem = getReasonFirstQuoteItem(item, itemIndex);
      const amount = getReasonFirstRefundAmount(quoteItem);
      const selectedMethod = item.customer_preferred_refund_method;
      const calculation = item.reason_resolution_config?.refund_calculation || {};
      const calculationType = String(calculation.type || "full").toLowerCase();
      const calculationLabel = calculationType === "fixed"
        ? `Fixed refund · ₹${calculation.value ?? calculation.amount ?? 0} per item`
        : calculationType === "percentage"
          ? `Partial refund · ${calculation.value ?? calculation.percentage ?? 0}% per item`
          : "Full refund";
      return {
        item,
        amount,
        calculationLabel,
        methodLabel: item.customer_preferred_refund_method
          ? refundModeName(item.customer_preferred_refund_method)
          : "Method not selected",
        methodUnavailable: Boolean(selectedMethod) && !isReasonFirstQuoteMethodAvailable(quoteItem, selectedMethod),
      };
    })
    .filter(Boolean);
  const renderReasonFirstTransferDetails = () => (
    <>
      {returnedProducts.some((item) => item.return_type === "refund" && item.reason_mode === "reason_first" && item.customer_preferred_refund_method === "bank_transfer") && (
        <div className="v2-refund-bank-inline-form v2-reason-first-transfer-form">
          <p className="v2-refund-bank-inline-title">Bank details for applicable items</p>
          {savedInlineRefundDetails.bank_transfer && inlineEditingMethod !== "bank_transfer" ? (
            <div className="v2-refund-inline-summary">
              <div>
                <p className="v2-refund-inline-success">{getInlineSavedLabel("bank_transfer")}</p>
                <p className="v2-refund-inline-value">
                  {savedInlineRefundDetails.bank_transfer.accountHolderName} - {savedInlineRefundDetails.bank_transfer.accountNumber.replace(/^(\d+)(\d{4})/, "*******$2")}
                </p>
              </div>
              <button type="button" className="v2-refund-inline-edit" onClick={() => isReasonFirstRefundFlow ? editReasonFirstTransferDetails("bank_transfer") : setInlineEditingMethod("bank_transfer")}>Edit</button>
            </div>
          ) : (
            <>
              <TextField variant="outlined" placeholder="Account holder name" value={inlineBankDetails.accountHolderName} error={Boolean(getVisibleInlineBankError("accountHolderName"))} helperText={getVisibleInlineBankError("accountHolderName")} onBlur={() => markInlineBankFieldTouched("accountHolderName")} onChange={(event) => { markInlineBankFieldTouched("accountHolderName"); syncInlineBankDetails({ ...inlineBankDetails, accountHolderName: event.target.value.replace(/[^A-Za-z ]/g, "") }); }} fullWidth size="small" />
              <TextField variant="outlined" placeholder="Account number" value={inlineBankDetails.accountNumber} error={Boolean(getVisibleInlineBankError("accountNumber"))} helperText={getVisibleInlineBankError("accountNumber")} onBlur={() => markInlineBankFieldTouched("accountNumber")} onChange={(event) => { markInlineBankFieldTouched("accountNumber"); syncInlineBankDetails({ ...inlineBankDetails, accountNumber: event.target.value.replace(/\D/g, "") }); }} fullWidth size="small" />
              <TextField variant="outlined" placeholder="Confirm account number" value={inlineBankDetails.confirmAccountNumber} error={Boolean(getVisibleInlineBankError("confirmAccountNumber"))} helperText={getVisibleInlineBankError("confirmAccountNumber")} onBlur={() => markInlineBankFieldTouched("confirmAccountNumber")} onPaste={(event) => event.preventDefault()} onDrop={(event) => event.preventDefault()} onChange={(event) => { markInlineBankFieldTouched("confirmAccountNumber"); syncInlineBankDetails({ ...inlineBankDetails, confirmAccountNumber: event.target.value.replace(/\D/g, "") }); }} fullWidth size="small" />
              <TextField variant="outlined" placeholder="IFSC code" value={inlineBankDetails.ifsc} error={Boolean(getVisibleInlineBankError("ifsc"))} helperText={getVisibleInlineBankError("ifsc")} onBlur={() => markInlineBankFieldTouched("ifsc")} onChange={(event) => syncInlineBankDetails({ ...inlineBankDetails, ifsc: event.target.value.toUpperCase() })} fullWidth size="small" />
              {verificationError && <p className="v2-refund-inline-error">{verificationError}</p>}
              <Button type="button" variant="contained" disabled={submittingDetails} className="v2-refund-inline-save" onClick={handleSaveInlineBankDetails}>{getInlineSubmitLabel()}</Button>
            </>
          )}
        </div>
      )}
      {returnedProducts.some((item) => item.return_type === "refund" && item.reason_mode === "reason_first" && item.customer_preferred_refund_method === "upi") && (
        <div className="v2-refund-bank-inline-form v2-reason-first-transfer-form">
          <p className="v2-refund-bank-inline-title">UPI details for applicable items</p>
          {savedInlineRefundDetails.upi && inlineEditingMethod !== "upi" ? (
            <div className="v2-refund-inline-summary">
              <div>
                <p className="v2-refund-inline-success">{getInlineSavedLabel("upi")}</p>
                <p className="v2-refund-inline-value">UPI ID - {savedInlineRefundDetails.upi.upi}</p>
              </div>
              <button type="button" className="v2-refund-inline-edit" onClick={() => isReasonFirstRefundFlow ? editReasonFirstTransferDetails("upi") : setInlineEditingMethod("upi")}>Edit</button>
            </div>
          ) : (
            <>
              <TextField variant="outlined" placeholder="UPI ID" value={inlineUpiValue} error={Boolean(visibleInlineUpiError)} helperText={visibleInlineUpiError} onBlur={() => setInlineUpiTouched(true)} onChange={(event) => { setInlineUpiTouched(true); syncInlineUpiDetails(event.target.value); }} fullWidth size="small" />
              {verificationError && <p className="v2-refund-inline-error">{verificationError}</p>}
              <Button type="button" variant="contained" disabled={submittingDetails} className="v2-refund-inline-save" onClick={handleSaveInlineUpiDetails}>{getInlineSubmitLabel()}</Button>
            </>
          )}
        </div>
      )}
    </>
  );

  useEffect(() => {
    if (!hasLegacyRefundProducts) return;
    if (
      reasonFirstRefundProducts.length > 0 &&
      selectedRefundSource &&
      !visibleRefundModeOptions.some((option) => option.value === selectedRefundSource)
    ) {
      dispatch(addRefundSource(null));
      dispatch(addRefundJSON({}));
      mergeReturnCartContext({ refundSource: null, refundJSON: {} });
    }
  }, [dispatch, hasLegacyRefundProducts, reasonFirstRefundProducts.length, selectedRefundSource, visibleRefundModeOptions]);
  const selectedRefundModeOption = useMemo(
    () =>
      visibleRefundModeOptions.find(
        (option) => option.value === selectedRefundSource,
      ),
    [selectedRefundSource, visibleRefundModeOptions],
  );
  useEffect(() => {
    if (!hasLegacyRefundProducts) return;
    if (
      visibleRefundModeOptions.length === 0 ||
      visibleRefundModeOptions.some(
        (option) => option.value === selectedRefundSource,
      )
    ) {
      return;
    }

    // Prevent mode snap-back when option list re-renders after a manual choice.
    if (hasUserSelectedRefundModeRef.current && selectedRefundSource) {
      return;
    }

    const nextRefundSource = visibleRefundModeOptions[0].value;
    dispatch(addRefundSource(nextRefundSource));

    if (
      visibleRefundModeOptions.length !== 1 ||
      (nextRefundSource !== "bank_transfer" && nextRefundSource !== "upi")
    ) {
      return;
    }

    if (!rmsConfig.use_direct_transfer_for_refund && shouldPopulateTransferDetails) {
      if (nextRefundSource === "upi") {
        saveLegacyRefundJSON("upi", { upi: { upi_code: upiValue } });
      } else {
        saveLegacyRefundJSON("bank_transfer", { bank_details: {
          bank_account_number: accountNumber,
          confirm_bank_account_number: confirmAccNumber,
          bank_account_holder_name: accHolderName,
          bank_ifsc_code: ifsc,
        } });
      }
    }

    if (shouldCollectRefundDetailsInline(nextRefundSource)) {
      if (savedInlineRefundDetails[nextRefundSource]) {
        if (nextRefundSource === "upi") {
          saveLegacyRefundJSON("upi", { upi: { upi_code: savedInlineRefundDetails.upi.upi } });
        } else {
          saveLegacyRefundJSON("bank_transfer", { bank_details: {
            bank_account_number: savedInlineRefundDetails.bank_transfer.accountNumber,
            confirm_bank_account_number: savedInlineRefundDetails.bank_transfer.confirmAccountNumber,
            bank_account_holder_name: savedInlineRefundDetails.bank_transfer.accountHolderName,
            bank_ifsc_code: savedInlineRefundDetails.bank_transfer.ifsc,
          } });
        }
        setInlineEditingMethod(null);
        return;
      }

      if (reasonFirstRefundProducts.length > 0 && hasLegacyRefundProducts) {
        const nextRefundJSON = { ...currentRefundJSON };
        delete nextRefundJSON[nextRefundSource === "upi" ? "upi" : "bank_details"];
        dispatch(addRefundJSON(nextRefundJSON));
        mergeReturnCartContext({ refundJSON: nextRefundJSON });
      } else {
        dispatch(addRefundJSON({}));
      }
      setInlineEditingMethod(nextRefundSource);
      return;
    }

  }, [
    accountNumber,
    accHolderName,
    confirmAccNumber,
    dispatch,
    ifsc,
    rmsConfig.offer_refund_mode_label,
    rmsConfig.use_direct_transfer_for_refund,
    selectedRefundSource,
    shouldPopulateTransferDetails,
    shouldCollectRefundDetailsInline,
    savedInlineRefundDetails,
    upiValue,
    visibleRefundModeOptions,
    hasLegacyRefundProducts,
    reasonFirstRefundProducts.length,
  ]);

  const renderRefundModeOption = (sourceValue, sourceLabel) => {
    const isHiddenAfterSelection =
      !refundModesExpanded &&
      selectedRefundModeOption &&
      selectedRefundSource !== sourceValue;

    return (
      <StyledPaymentMode
        key={sourceValue}
        as="div"
        role="radio"
        aria-checked={selectedRefundSource === sourceValue}
        tabIndex={0}
        className={`v2-refund-mode-card ${
          isHiddenAfterSelection ? "v2-refund-mode-card-hidden" : ""
        }`}
        primary_color={merchantConfig.primary_theme_color}
        onClick={() => {
          updateRefundSource(sourceValue, true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            updateRefundSource(sourceValue, true);
          }
        }}
        selected={selectedRefundSource === sourceValue}
        style={{
          borderRadius: merchantConfig.use_rounded_edge_buttons
            ? "10px"
            : "0px",
        }}
        bonus={paymentDetails?.offer_refund_mode === sourceValue}
        charges={getCharges(sourceValue, paymentDetails)}
        $hasOfferText={Boolean(getRefundMethodDisplay(sourceValue)?.offer_text)}
      >
        <span className="v2-refund-mode-icon">{renderIcon(sourceValue)}</span>
        <span className="v2-refund-mode-copy">
          <span className="v2-refund-mode-title-row">
            <span className="v2-refund-mode-title">
              {getRefundModeTitle(sourceValue, sourceLabel)}
            </span>
            {getCharges(sourceValue, paymentDetails) > 0 && (
              <ChargesSection source={sourceValue} />
            )}
            {paymentDetails?.offer_refund_mode === sourceValue &&
              BonusChargesSection}
          </span>
          <span className="v2-refund-mode-description">
            {getRefundModeDescription(sourceValue)}
          </span>
        </span>
        <CheckmarkControl
          className="v2-checkmark-control"
          primaryColor={merchantConfig.primary_theme_color}
          selected={selectedRefundSource === sourceValue}
          size={18}
        />
        {getRefundMethodDisplay(sourceValue)?.offer_text && (
          <span className="v2-refund-mode-offer">
            {getRefundMethodDisplay(sourceValue).offer_text}
          </span>
        )}
        {sourceValue === "bank_transfer" &&
          selectedRefundSource === "bank_transfer" &&
          shouldCollectRefundDetailsInline(sourceValue) &&
          savedInlineRefundDetails.bank_transfer &&
          inlineEditingMethod !== "bank_transfer" && (
            <div
              className="v2-refund-bank-inline-form"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="v2-refund-bank-inline-divider" />
              <div className="v2-refund-inline-summary">
                <div>
                  <p className="v2-refund-inline-success">
                    {getInlineSavedLabel("bank_transfer")}
                  </p>
                  <p className="v2-refund-inline-value">
                    {savedInlineRefundDetails.bank_transfer.accountHolderName}{" "}
                    -{" "}
                    {savedInlineRefundDetails.bank_transfer.accountNumber.replace(
                      /^(\d+)(\d{4})/,
                      "*******$2",
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="v2-refund-inline-edit"
                  onClick={(event) => {
                    event.stopPropagation();
                    setInlineEditingMethod("bank_transfer");
                    dispatch(addRefundJSON({}));
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          )}
        {sourceValue === "bank_transfer" &&
          selectedRefundSource === "bank_transfer" &&
          shouldCollectRefundDetailsInline(sourceValue) &&
          (!savedInlineRefundDetails.bank_transfer ||
            inlineEditingMethod === "bank_transfer") && (
            <div
              className="v2-refund-bank-inline-form"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="v2-refund-bank-inline-divider" />
              <p className="v2-refund-bank-inline-title">
                Enter bank details
              </p>
              <TextField
                variant="outlined"
                placeholder="Account holder name"
                value={inlineBankDetails.accountHolderName}
                error={Boolean(getVisibleInlineBankError("accountHolderName"))}
                helperText={getVisibleInlineBankError("accountHolderName")}
                onBlur={() => markInlineBankFieldTouched("accountHolderName")}
                onChange={(event) => {
	                  markInlineBankFieldTouched("accountHolderName");
	                  const accountHolderName = event.target.value.replace(
	                    /[^A-Za-z ]/g,
	                    "",
	                  );
	                  syncInlineBankDetails({
	                    ...inlineBankDetails,
	                    accountHolderName,
	                  });
	                }}
                fullWidth
                size="small"
              />
              <TextField
                variant="outlined"
                placeholder="Account number"
                value={inlineBankDetails.accountNumber}
                error={Boolean(getVisibleInlineBankError("accountNumber"))}
                helperText={getVisibleInlineBankError("accountNumber")}
                onBlur={() => markInlineBankFieldTouched("accountNumber")}
                onChange={(event) => {
                  markInlineBankFieldTouched("accountNumber");
                  const accountValue = event.target.value.replace(/\D/g, "");
                  syncInlineBankDetails({
                    ...inlineBankDetails,
                    accountNumber: accountValue,
                  });
                }}
                fullWidth
                size="small"
              />
              <TextField
                variant="outlined"
                placeholder="Confirm account number"
                value={inlineBankDetails.confirmAccountNumber}
                error={Boolean(
                  getVisibleInlineBankError("confirmAccountNumber"),
                )}
                helperText={getVisibleInlineBankError("confirmAccountNumber")}
                onBlur={() =>
                  markInlineBankFieldTouched("confirmAccountNumber")
                }
                onPaste={(event) => event.preventDefault()}
                onDrop={(event) => event.preventDefault()}
                onChange={(event) => {
                  markInlineBankFieldTouched("confirmAccountNumber");
                  const accountValue = event.target.value.replace(/\D/g, "");
                  syncInlineBankDetails({
                    ...inlineBankDetails,
                    confirmAccountNumber: accountValue,
                  });
                }}
                fullWidth
                size="small"
              />
              <TextField
                variant="outlined"
                placeholder="IFSC code"
                value={inlineBankDetails.ifsc}
                error={Boolean(getVisibleInlineBankError("ifsc"))}
                helperText={getVisibleInlineBankError("ifsc")}
                onBlur={() => markInlineBankFieldTouched("ifsc")}
                onChange={(event) => {
                  markInlineBankFieldTouched("ifsc");
                  syncInlineBankDetails({
                    ...inlineBankDetails,
                    ifsc: event.target.value.toUpperCase(),
                  });
                }}
                fullWidth
                size="small"
              />
              {verificationError && (
                <p className="v2-refund-inline-error">{verificationError}</p>
              )}
              <Button
                type="button"
                variant="contained"
                disabled={submittingDetails}
                className="v2-refund-inline-save"
                onClick={handleSaveInlineBankDetails}
              >
                {getInlineSubmitLabel()}
              </Button>
            </div>
          )}
        {sourceValue === "upi" &&
          selectedRefundSource === "upi" &&
          shouldCollectRefundDetailsInline(sourceValue) &&
          savedInlineRefundDetails.upi &&
          inlineEditingMethod !== "upi" && (
            <div
              className="v2-refund-bank-inline-form"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="v2-refund-bank-inline-divider" />
              <div className="v2-refund-inline-summary">
                <div>
                  <p className="v2-refund-inline-success">
                    {getInlineSavedLabel("upi")}
                  </p>
                  <p className="v2-refund-inline-value">
                    UPI ID - {savedInlineRefundDetails.upi.upi}
                  </p>
                </div>
                <button
                  type="button"
                  className="v2-refund-inline-edit"
                  onClick={(event) => {
                    event.stopPropagation();
                    setInlineEditingMethod("upi");
                    dispatch(addRefundJSON({}));
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          )}
        {sourceValue === "upi" &&
          selectedRefundSource === "upi" &&
          shouldCollectRefundDetailsInline(sourceValue) &&
          (!savedInlineRefundDetails.upi || inlineEditingMethod === "upi") && (
            <div
              className="v2-refund-bank-inline-form"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="v2-refund-bank-inline-divider" />
              <p className="v2-refund-bank-inline-title">Enter UPI details</p>
              <TextField
                variant="outlined"
                placeholder="UPI ID"
                value={inlineUpiValue}
                error={Boolean(visibleInlineUpiError)}
                helperText={visibleInlineUpiError}
                onBlur={() => setInlineUpiTouched(true)}
                onChange={(event) => {
                  setInlineUpiTouched(true);
                  syncInlineUpiDetails(event.target.value);
                }}
                fullWidth
                size="small"
              />
              {verificationError && (
                <p className="v2-refund-inline-error">{verificationError}</p>
              )}
              <Button
                type="button"
                variant="contained"
                disabled={submittingDetails}
                className="v2-refund-inline-save"
                onClick={handleSaveInlineUpiDetails}
              >
                {getInlineSubmitLabel()}
              </Button>
            </div>
          )}
      </StyledPaymentMode>
    );
  };

  return (
    <div className="box" id="Returnmode">
      <div>
        {isReasonFirstOnlyRefundFlow ? renderReasonFirstTransferDetails() : <>
        {isReasonFirstRefundFlow && renderReasonFirstTransferDetails()}
        <div className="v2-refund-method-container">
          <div className="v2-refund-method-header">
            <p className="title v2-refund-method-title">Refund Method</p>
            {!refundModesExpanded && selectedRefundModeOption ? (
              <button
                className="v2-refund-mode-change"
                onClick={() => setRefundModesExpanded(true)}
                type="button"
              >
                Change
                <KeyboardArrowDownIcon />
              </button>
            ) : null}
          </div>
          <div
            className={`v2-refund-mode-list ${
              refundModesExpanded || !selectedRefundModeOption
                ? "expanded"
                : "collapsed"
            }`}
            role="radiogroup"
          >
            {visibleRefundModeOptions.map((option) =>
              renderRefundModeOption(option.value, option.label),
            )}
          </div>
        </div>
        </>}

        {calculatingLoader ? (
          <V2PaymentDetailsBoneyardLoader />
        ) : (
          paymentDetails &&
          paymentDetails !== null && (
            <StyledPaymentDiv>
              <p className="payment-title">Refund Details</p>
              {reasonFirstRefundBreakdown.length > 0 && (
                <div className="v2-refund-details-item-breakdown" aria-label="Refund details by item">
                  {reasonFirstRefundBreakdown.map(({ item, amount, calculationLabel, methodLabel, methodUnavailable }, index) => (
                    <div className="v2-refund-details-item" key={`${item.line_item_id}-${index}`}>
                      <div className="v2-refund-details-item-main">
                        <p>{item.product_title || "Product"}</p>
                        <strong>{amount === null ? "Calculating…" : `₹ ${amount.toFixed(2)}`}</strong>
                      </div>
                      <p className="v2-refund-details-item-rule">{calculationLabel} - {methodLabel}</p>
                      {methodUnavailable && (
                        <p className="v2-refund-details-item-error" role="alert">
                          This method is unavailable for the calculated refund. Go back to this item and choose another method.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="subtotal-statement">
                <p>Subtotal of Products (Refund)</p>
                <p
                  style={{
                    color: "3B444D",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                    marginLeft: "1rem",
                  }}
                >
                  ₹ {paymentDetails.refund_product_total}
                </p>
              </div>

              {!isIykaPolicyQuote(paymentDetails) && paymentDetails.refund_charges +
                paymentDetails.preapplied_refund_charge >
                0 && (
                <div className="subtotal-statement">
                  <p>
                    {paymentDetails.refund_charge_label &&
                    paymentDetails.refund_charge_label !== ""
                      ? paymentDetails.refund_charge_label
                      : "Handling Charges"}
                  </p>
                  <p
                    style={{
                      color: "var(--rms-v2-danger)",
                      fontWeight: "500",
                      marginLeft: "1rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ₹{" "}
                    {paymentDetails.refund_charges +
                      paymentDetails.preapplied_refund_charge}
                  </p>
                </div>
              )}

              {!isIykaPolicyQuote(paymentDetails) && paymentDetails.shipping_charges > 0 && (
                <div className="subtotal-statement">
                  <p>
                    {paymentDetails.shipping_charge_label &&
                    paymentDetails.shipping_charge_label !== ""
                      ? paymentDetails.shipping_charge_label
                      : "Shipping Charges"}
                  </p>
                  <p
                    style={{
                      color: "var(--rms-v2-danger)",
                      fontWeight: "500",
                      marginLeft: "1rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ₹ {paymentDetails.shipping_charges}
                  </p>
                </div>
              )}

              <IykaRefundBreakdown paymentDetails={paymentDetails} />

              {!isIykaPolicyQuote(paymentDetails) && (paymentDetails.refund_mode_offer_amount > 0 ||
                paymentDetails.item_level_refund_mode_offer_amount > 0) &&
                paymentDetails.offer_refund_mode === refundSource && (
                  <div className="subtotal-statement">
                    <p>Bonus</p>
                    <p
                      style={{
                        color: "var(--rms-v2-success)",
                        fontWeight: "500",
                        marginLeft: "1rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      + ₹{" "}
                      {paymentDetails.offer_refund_mode_on === "item_level"
                        ? paymentDetails.total_refund_mode_offer_amount
                        : paymentDetails.refund_mode_offer_amount}
                    </p>
                  </div>
                )}

              {paymentDetails.refund_amount >= 0 && (
                <div
                  style={{
                    borderTop: "1px dashed var(--rms-v2-text-muted)",
                    padding: "0.5rem 0px",
                    margin: "1rem 0 0",
                    fontWeight: 700,
                    color: merchantConfig.primary_theme_color,
                  }}
                  className="subtotal-statement"
                >
                  <Fragment>
                    <p>Estimated Refund Amount</p>
                    <p
                      style={{
                        marginLeft: "1rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {calculatingLoader
                        ? "Calculating..."
                        : `₹ ${paymentDetails.refund_amount}`}
                    </p>
                  </Fragment>
                </div>
              )}
              {refundDisplay?.important_note?.description && (
                <aside
                  className="v2-refund-important-note"
                  aria-label={refundDisplay.important_note.title}
                >
                  <InfoIcon aria-hidden="true" />
                  <div>
                    <p className="v2-refund-important-note-title">
                      {refundDisplay.important_note.title}
                    </p>
                    <p>{refundDisplay.important_note.description}</p>
                  </div>
                </aside>
              )}
            </StyledPaymentDiv>
          )
        )}
      </div>
    </div>
  );
};
